// /finance's Business expenses (finance redesign slice 3): what running BeNerdy costs, in one list. Each repeating expense
// with its last bill and its next, each bill paid once, and each month of the agency's own ad spend (TD-105); a bill that
// fell due with no amount comes first, then one to pay, in the server's order. A cost passed along to a client is not
// here unless it comes out of a pot: it rides that client's months (J14). No strip, no search (J24), no filter bar (995e581c). The shared totals
// footer, pinned at the bottom, holds what running BeNerdy costs each month, what went out this month and the cost to
// BeNerdy of it (finance-real round 2). A row opens its bill when one is due (Record bill), else its expense, the bill paid
// once, or the month of ads. A repeating row's ⋮ holds Edit, Stop and Delete, each the expense's drawer at that step
// (TD-099). Every figure is the server's (TD-102).
// finance-real round 8 (slice 9, J41, J42): each pot is one row, first, holding its charges and its repeating expenses:
// this month's paid and what went to clients, and what is not assigned since its first charge. It opens the pot's
// drawer and has no ⋮. Its row reuses the Repeats and Next bill columns, for whom it is shared with and what it has not
// assigned (slice 12 R21).
import { TablePageBody } from "@/components/PageLayout/TablePageBody";
import { Badge } from "@/components/ui/Badge";
import { DataTable, type DataColumn } from "@/components/ui/DataTable";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { TabPending } from "../components/TabPending";
import { TotalsFooter, type TotalItem } from "../components/TotalsFooter";
import { useAgencyExpenses } from "../hooks/useAgencyFinance";
import type { ParamPatch } from "../hooks/useFinanceParams";
import type { AgencyExpenseRow, ExpenseTotals, Pot } from "../types";
import { CADENCE_LABELS, cadenceText, templateActions, type TemplateStep } from "../utils/expenses";
import { dateLabel, monthLabel } from "../utils/format";
import { NotAssigned, potMonthText } from "./potText";

const EXPENSE = ENTITY_CONFIG.expense;
const expenseWord = EXPENSE.label.toLowerCase();
const expensesWord = `business ${EXPENSE.plural.toLowerCase()}`;
const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const clientsWord = ENTITY_CONFIG.client.plural.toLowerCase();
const STEP_LABELS: Record<TemplateStep, string> = { stop: `Stop ${expenseWord}`, delete: `Delete ${expenseWord}` };

/** The Repeats column: a repeating expense's cadence, a bill paid once, or the month a month of ads is. */
export function repeatsText(r: AgencyExpenseRow): string {
  if (r.kind === "repeating") return cadenceText(r);
  return r.kind === "ads" ? monthLabel(r.date.slice(0, 7)) : CADENCE_LABELS.once;
}

/** A row's click: the bill due (Record bill), else the expense; a bill paid once or a month of ads, its own drawer. */
export function expenseTarget(r: AgencyExpenseRow): ParamPatch {
  if (r.kind === "ads") return { modal: "ads", entry: r.id };
  if (r.kind === "once") return { modal: "expense", entry: r.id };
  return r.due ? { modal: "expense", entry: r.due.billId } : { modal: "expense-item", tpl: r.id };
}

/** A repeating row's ⋮ (J8): its drawer, and today's Stop and Delete where they still apply (TD-099). */
export function expenseMenu(r: AgencyExpenseRow, onOpen: (patch: ParamPatch) => void): DropdownMenuEntry[] {
  if (r.kind !== "repeating") return [];
  const steps: DropdownMenuEntry[] = templateActions(r).map((step) => ({
    id: step,
    label: STEP_LABELS[step],
    variant: "danger" as const,
    onClick: () => onOpen({ modal: "expense-item", tpl: r.id, confirm: step }),
  }));
  return [{ id: "edit", label: `Edit ${expenseWord}`, onClick: () => onOpen({ modal: "expense-item", tpl: r.id }) }, ...steps];
}

function NextCell({ r }: { r: AgencyExpenseRow }) {
  if (r.kind !== "repeating") return <span className="text-gray-400">·</span>;
  if (r.due) {
    return <Badge tone="amber">{r.due.missing ? `No amount · ${dateLabel(r.due.date)}` : `To pay · ${dateLabel(r.due.date)}`}</Badge>;
  }
  if (r.endsOn) return <span className="text-gray-500">Stopped</span>;
  return <span className="text-gray-600">{dateLabel(r.nextBill)}</span>;
}

/** A row of the table: a pot, or one of the rows the server lists. */
type Row = { kind: "pot"; id: string; pot: Pot } | AgencyExpenseRow;

const Last = ({ last }: { last: { date: string; amountMinor: number } | null }) =>
  last ? (
    <span className="whitespace-nowrap">
      <MoneyText amountMinor={last.amountMinor} /> <span className="hidden sm:inline text-xs text-gray-400">{dateLabel(last.date)}</span>
    </span>
  ) : (
    <span className="text-gray-400">·</span>
  );

/** Whom a pot is shared with: the clients other than the agency's own it passed slices to since its first charge. */
const sharedText = (pot: Pot): string => (pot.clients > 0 ? `Shared with ${pot.clients} ${pot.clients === 1 ? clientWord : clientsWord}` : "Shared");

const columns: DataColumn<Row>[] = [
  {
    key: "label", label: EXPENSE.label,
    render: (r) =>
      r.kind === "pot" ? (
        <span className="flex flex-col min-w-0">
          <span className="font-medium text-gray-900">{r.pot.label}</span>
          <span className="text-xs text-gray-500 truncate">{potMonthText(r.pot.thisMonth)}</span>
        </span>
      ) : (
        <span className="flex flex-col">
          <span className="font-medium text-gray-900">{r.label}</span>
          {r.clientName && <span className="text-xs text-gray-500">{r.clientName}</span>}
        </span>
      ),
  },
  {
    key: "category", label: ENTITY_CONFIG.expenseCategory.label, width: "hidden sm:table-cell w-36",
    render: (r) => (r.kind === "pot" ? <Badge tone="blue">Pot</Badge> : <span className="text-gray-600">{r.categoryLabel}</span>),
  },
  {
    key: "repeats", label: "Repeats", width: "hidden sm:table-cell",
    render: (r) => <span className="text-gray-600">{r.kind === "pot" ? sharedText(r.pot) : repeatsText(r)}</span>,
  },
  { key: "last", label: "Last bill", align: "right", width: "w-32 sm:w-44", render: (r) => <Last last={r.kind === "pot" ? r.pot.last : r.last} /> },
  { key: "next", label: "Next bill", width: "w-36 sm:w-56", render: (r) => (r.kind === "pot" ? <NotAssigned pot={r.pot} /> : <NextCell r={r} />) },
];

/** The footer (finance-real round 2): what running BeNerdy costs each month, and what went out this month. */
const totalsOf = (totals: ExpenseTotals): TotalItem[] => [
  { label: "Each month", amountMinor: totals.monthlyMinor, note: "repeating, a yearly one as a twelfth, pots apart" },
  { label: `Paid in ${monthLabel(totals.month)}`, amountMinor: totals.paidMinor, note: "with the pots' charges" },
  { label: "Cost to BeNerdy", amountMinor: totals.costMinor, note: `less what the pots passed to ${clientsWord}` },
];

export function BusinessExpensesTab({ onOpen }: { onOpen: (patch: ParamPatch) => void }) {
  const query = useAgencyExpenses();
  if (!query.data) {
    return (
      <TablePageBody>
        <TabPending failed={query.isError} message={`Could not load the ${expensesWord}.`} retrying={query.isFetching} onRetry={() => void query.refetch()} />
      </TablePageBody>
    );
  }
  return (
    <TablePageBody>
      <DataTable
        columns={columns}
        rows={[...query.data.pots.map((pot): Row => ({ kind: "pot", id: pot.id, pot })), ...query.data.rows]}
        rowKey={(r) => `${r.kind}:${r.id}`}
        onRowClick={(r) => onOpen(r.kind === "pot" ? { modal: "pot", category: r.id } : expenseTarget(r))}
        rowMenu={(r) => (r.kind === "pot" ? [] : expenseMenu(r, onOpen))}
        empty={`No ${expensesWord} yet. Add the first with ${EXPENSE.createLabel}.`}
        footer={<TotalsFooter items={totalsOf(query.data.totals)} />}
      />
    </TablePageBody>
  );
}
